// <s-marquee> — seamless continuous scroll of its [data-marquee-track] content.
// Clones the track's items once for a gapless loop (clones are aria-hidden + inert so links
// aren't announced or tabbed twice) and animates with the Web Animations API.
// Pause sources are tracked independently: hover, focus-within, hidden tab, and an explicit
// pause (sticky until the play command). Respects prefers-reduced-motion (no animation).
// Commands: --toggle (pause/play button). One component per file.
class SMarquee extends HTMLElement {
  #animation = null;
  #ac = null;
  #mql = null;
  #hover = false;
  #focus = false;
  #userPaused = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#setup(), { signal });

    this.addEventListener("pointerenter", () => { this.#hover = true; this.#sync(); }, { signal });
    this.addEventListener("pointerleave", () => { this.#hover = false; this.#sync(); }, { signal });
    // Focus on the pause button itself must not count as a pause source, or Play could never resume.
    this.addEventListener("focusin", (event) => {
      this.#focus = !event.target.closest("[data-marquee-toggle]");
      this.#sync();
    }, { signal });
    this.addEventListener("focusout", (event) => {
      if (!this.contains(event.relatedTarget) || event.relatedTarget.closest("[data-marquee-toggle]")) { this.#focus = false; this.#sync(); }
    }, { signal });
    document.addEventListener("visibilitychange", () => this.#sync(), { signal });
    this.addEventListener("command", (event) => {
      if (event.command === "--toggle") {
        this.#userPaused = !this.#userPaused;
        this.#sync();
      }
    }, { signal });

    this.#setup();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#animation?.cancel();
  }

  #setup() {
    this.#animation?.cancel();
    this.#animation = null;

    const track = this.querySelector("[data-marquee-track]");
    const toggle = this.#toggle();
    if (toggle) toggle.hidden = this.#mql.matches;
    if (!track || this.#mql.matches) return;

    // Ensure exactly two copies of the content so the loop is seamless.
    if (!track.dataset.cloned) {
      const clones = [...track.children].map((node) => {
        const clone = node.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        clone.inert = true;
        return clone;
      });
      track.append(...clones);
      track.dataset.cloned = "true";
    }

    // Measure one full repeat (first original to first clone) so the seam matches the gap exactly.
    const items = track.children;
    const half = items.length / 2;
    const distance = Math.abs(items[half].offsetLeft - items[0].offsetLeft);
    if (distance <= 0) return;

    const speed = parseFloat(this.dataset.speed) || 40;
    const durationMs = (distance / speed) * 1000;
    // In RTL the flex row starts at the right edge, so the travel direction flips.
    const rtl = getComputedStyle(this).direction === "rtl";
    const reverse = (this.dataset.direction === "right") !== rtl;
    const from = reverse ? -distance : 0;
    const to = reverse ? 0 : -distance;
    const sign = rtl ? -1 : 1;

    this.#animation = track.animate(
      [{ transform: `translateX(${from * sign}px)` }, { transform: `translateX(${to * sign}px)` }],
      { duration: durationMs, iterations: Infinity, easing: "linear" },
    );
    this.#sync();
  }

  // The pause button lives outside the marquee (so hovering it does not pause the strip) and targets it with commandfor.
  #toggle() {
    return this.id ? this.getRootNode().querySelector(`[data-marquee-toggle][commandfor="${this.id}"]`) : null;
  }

  #sync() {
    const toggle = this.#toggle();
    if (toggle) {
      toggle.setAttribute("aria-pressed", String(this.#userPaused));
      toggle.querySelector("[data-marquee-pause-icon]")?.toggleAttribute("hidden", this.#userPaused);
      toggle.querySelector("[data-marquee-play-icon]")?.toggleAttribute("hidden", !this.#userPaused);
    }
    if (!this.#animation) return;
    const paused = this.#userPaused || this.#hover || this.#focus || document.hidden;
    if (paused) this.#animation.pause();
    else this.#animation.play();
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
